class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances = document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.setupEventListeners();
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));
    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() { return this.input.value.trim(); }

  normalize(value) {
    let text = String(value || '').trim().toLowerCase();
    try { text = text.normalize('NFD').replace(/[\u0300-\u036f]/g, ''); } catch (error) {}
    return text.replace(/[^a-z0-9]+/g, ' ').replace(/\s+/g, ' ').trim();
  }

  words(value) { return this.normalize(value).split(' ').filter(Boolean); }

  isGenericSearch(value) {
    const generic = new Set([
      'sofa','sofas','bed','beds','dining','table','tables','chair','chairs','light','lights','lighting','lamp','lamps',
      'recliner','recliners','console','consoles','outdoor','bar','bars','office','tv','unit','units','coffee','side',
      'center','centre','stool','stools','cabinet','cabinets','desk','desks','bookcase','bookcases','pouffe','pouffes',
      'dresser','dressers','wardrobe','wardrobes','trolley','trolleys','rug','rugs','carpet','carpets','mirror','mirrors',
      'cushion','cushions','painting','paintings','art','bench','benches','showcase','showcases'
    ]);
    const list = this.words(value);
    return list.length > 0 && list.every((word) => generic.has(word));
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      this.querySelector('#predictive-search-results-groups-wrapper')?.remove();
    }
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);
    this.searchTerm = newSearchTerm;
    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }
    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();
    if (!currentSearchTerm.length) return;
    if (this.searchTerm !== currentSearchTerm) this.onChange();
    else if (this.getAttribute('results') === 'true') this.open();
    else this.getSearchResults(this.searchTerm);
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();
    switch (event.code) {
      case 'ArrowUp': this.switchOption('up'); break;
      case 'ArrowDown': this.switchOption('down'); break;
      case 'Enter': this.selectOption(); break;
    }
  }

  onKeydown(event) {
    if (event.code === 'ArrowUp' || event.code === 'ArrowDown') event.preventDefault();
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector('[data-predictive-search-search-for-text]');
    const currentButtonText = searchForTextElement?.innerText;
    if (!currentButtonText || !previousTerm) return;
    const escapedPrevious = previousTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const matches = currentButtonText.match(new RegExp(escapedPrevious, 'g'));
    if (matches && matches.length > 1) return;
    searchForTextElement.innerText = currentButtonText.replace(previousTerm, newTerm);
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;
    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');
    const allVisibleElements = Array.from(this.querySelectorAll('li, button.predictive-search__item')).filter(
      (element) => element.offsetParent !== null
    );
    if (!allVisibleElements.length || (moveUp && !selectedElement)) return;
    const selectedElementIndex = selectedElement ? allVisibleElements.indexOf(selectedElement) : -1;
    let activeElementIndex = 0;
    this.statusElement.textContent = '';
    if (!moveUp && selectedElement) activeElementIndex = selectedElementIndex === allVisibleElements.length - 1 ? 0 : selectedElementIndex + 1;
    else if (moveUp) activeElementIndex = selectedElementIndex === 0 ? allVisibleElements.length - 1 : selectedElementIndex - 1;
    if (activeElementIndex === selectedElementIndex) return;
    const activeElement = allVisibleElements[activeElementIndex];
    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector('[aria-selected="true"] a, button[aria-selected="true"]');
    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    const requested = String(searchTerm || '').trim();
    if (!requested) return;
    this.setLiveRegionLoadingState();
    if (!this.isGenericSearch(requested) && this.normalize(requested).length >= 2) {
      this.getIndexedTitleResults(requested);
      return;
    }
    this.getShopifyPredictiveResults(requested);
  }

  getIndexedTitleResults(requested) {
    const key = `avian-v8-index:${this.normalize(requested)}`;
    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }
    this.loadProductTitleIndex()
      .then((index) => {
        if (this.normalize(this.getQuery()) !== this.normalize(requested)) return;
        const matches = this.rankIndexMatches(index, requested).slice(0, 10);
        if (!matches.length) {
          this.getShopifyPredictiveResults(requested, true);
          return;
        }
        const markup = this.buildIndexResultsMarkup(matches, requested);
        this.allPredictiveSearchInstances.forEach((instance) => { instance.cachedResults[key] = markup; });
        this.renderSearchResults(markup);
      })
      .catch(() => this.getShopifyPredictiveResults(requested, true));
  }

  loadProductTitleIndex() {
    if (window.__avianProductTitleIndexV8) return Promise.resolve(window.__avianProductTitleIndexV8);
    if (window.__avianProductTitleIndexPromiseV8) return window.__avianProductTitleIndexPromiseV8;
    const storageKey = 'avianProductTitleIndexV8';
    try {
      const stored = sessionStorage.getItem(storageKey);
      if (stored) {
        const parsed = JSON.parse(stored);
        if (Array.isArray(parsed) && parsed.length) {
          window.__avianProductTitleIndexV8 = parsed;
          return Promise.resolve(parsed);
        }
      }
    } catch (error) {}
    window.__avianProductTitleIndexPromiseV8 = this.fetchIndexPage(1)
      .then((firstPage) => {
        const pages = Math.max(1, Number(firstPage.pages) || 1);
        const requests = [];
        for (let page = 2; page <= pages; page += 1) requests.push(this.fetchIndexPage(page));
        return Promise.all(requests).then((rest) => [firstPage, ...rest]);
      })
      .then((pageData) => {
        const seen = new Set();
        const index = [];
        pageData.forEach((page) => {
          (page.products || []).forEach((product) => {
            if (!product || !product.handle || seen.has(product.handle)) return;
            seen.add(product.handle);
            index.push(product);
          });
        });
        window.__avianProductTitleIndexV8 = index;
        try { sessionStorage.setItem(storageKey, JSON.stringify(index)); } catch (error) {}
        return index;
      })
      .finally(() => { window.__avianProductTitleIndexPromiseV8 = null; });
    return window.__avianProductTitleIndexPromiseV8;
  }

  fetchIndexPage(page) {
    const root = window.Shopify?.routes?.root || '/';
    const url = new URL(`${root}collections/all`, location.origin);
    url.searchParams.set('sort_by', 'created-descending');
    url.searchParams.set('section_id', 'avian-search-index');
    url.searchParams.set('page', String(page));
    return fetch(url.toString(), { credentials: 'same-origin' })
      .then((response) => {
        if (!response.ok) throw new Error(`Index ${response.status}`);
        return response.text();
      })
      .then((text) => {
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const node = doc.querySelector('[data-avian-search-index]');
        if (!node) throw new Error('Search index section missing');
        return JSON.parse(node.textContent || '{}');
      });
  }

  rankIndexMatches(index, query) {
    const q = this.normalize(query);
    const qWords = this.words(query);
    const generic = new Set(['sofa','sofas','bed','beds','table','tables','chair','chairs','lamp','lamps','light','lights','unit','units','bar','bars','console','consoles']);
    const specific = qWords.filter((word) => !generic.has(word));
    const anchors = specific.length ? specific : qWords;
    return index
      .map((product, originalIndex) => {
        const title = this.normalize(product.title);
        const titleWords = this.words(product.title);
        let score = 99;
        if (title === q) score = 0;
        else if (title.startsWith(q)) score = 1;
        else if (qWords.length && qWords.every((word) => titleWords.some((titleWord) => titleWord.startsWith(word)))) score = 2;
        else if (anchors.length && anchors.every((word) => titleWords.some((titleWord) => titleWord.startsWith(word)))) score = 3;
        return { product, score, originalIndex };
      })
      .filter((entry) => entry.score < 99)
      .sort((a, b) => a.score - b.score || a.originalIndex - b.originalIndex || a.product.title.length - b.product.title.length)
      .map((entry) => entry.product);
  }

  getShopifyPredictiveResults(requested, titleOnly = false) {
    const key = `avian-v8-shopify:${titleOnly ? 'title' : 'generic'}:${this.normalize(requested)}`;
    if (this.cachedResults[key]) {
      this.renderSearchResults(this.cachedResults[key]);
      return;
    }
    if (this.abortController) this.abortController.abort();
    this.abortController = new AbortController();
    const params = new URLSearchParams();
    params.set('q', requested);
    params.set('resources[type]', 'product');
    params.set('resources[limit]', '10');
    params.set('resources[options][unavailable_products]', 'last');
    params.set('resources[options][fields]', titleOnly ? 'title' : 'title,product_type,variants.title,variants.sku');
    params.set('resources[options][prefix]', 'last');
    params.set('section_id', 'predictive-search');
    fetch(`${routes.predictive_search_url}?${params.toString()}`, { signal: this.abortController.signal })
      .then((response) => {
        if (!response.ok) throw new Error(String(response.status));
        return response.text();
      })
      .then((text) => {
        const section = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-predictive-search');
        if (!section) throw new Error('Predictive search section not found');
        return section.innerHTML;
      })
      .catch((error) => {
        if (error?.code === 20 || error?.name === 'AbortError') return null;
        return null;
      })
      .then((markup) => {
        if (!markup || this.normalize(this.getQuery()) !== this.normalize(requested)) return;
        this.allPredictiveSearchInstances.forEach((instance) => { instance.cachedResults[key] = markup; });
        this.renderSearchResults(markup);
      });
  }

  buildIndexResultsMarkup(products, searchTerm) {
    const searchLabel = this.escapeMarkup(searchTerm);
    const root = window.Shopify?.routes?.root || '/';
    const items = products.map((product, index) => {
      const title = this.escapeMarkup(product.title);
      const handle = this.escapeMarkup(product.handle);
      const image = product.image ? this.escapeMarkup(product.image) : '';
      const imageMarkup = image ? `<img class="predictive-search__image" src="${image}" alt="${title}" width="50" height="50" loading="lazy">` : '';
      return `<li id="predictive-search-option-product-${index + 1}" class="predictive-search__list-item" role="option" aria-selected="false"><a href="${root}products/${handle}" class="predictive-search__item predictive-search__item--link-with-thumbnail link link--text" tabindex="-1">${imageMarkup}<div class="predictive-search__item-content predictive-search__item-content--centered"><p class="predictive-search__item-heading h5">${title}</p></div></a></li>`;
    }).join('');
    const count = products.length;
    return `<div id="predictive-search-results" role="listbox"><div id="predictive-search-results-groups-wrapper" class="predictive-search__results-groups-wrapper predictive-search__results-groups-wrapper--no-suggestions"><div class="predictive-search__result-group"><div><h2 id="predictive-search-products" class="predictive-search__heading text-body caption-with-letter-spacing">Products</h2><ul id="predictive-search-results-products-list" class="predictive-search__results-list list-unstyled" role="group" aria-labelledby="predictive-search-products">${items}</ul></div></div></div><div id="predictive-search-option-search-keywords" class="predictive-search__search-for-button"><button class="predictive-search__item predictive-search__item--term link link--text h5 animate-arrow" tabindex="-1" role="option" aria-selected="false"><span data-predictive-search-search-for-text>Search for “${searchLabel}”</span></button></div></div><span class="hidden" data-predictive-search-live-region-count-value>${count} product result${count === 1 ? '' : 's'}</span>`;
  }

  escapeMarkup(value) {
    return String(value ?? '').replace(/[&<>"']/g, (character) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    })[character]);
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');
    this.setLiveRegionText(this.loadingText);
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) return;
    this.statusElement.setAttribute('aria-hidden', 'false');
    this.statusElement.textContent = statusText || '';
    setTimeout(() => this.statusElement?.setAttribute('aria-hidden', 'true'), 1000);
  }

  renderSearchResults(resultsMarkup) {
    this.predictiveSearchResults.innerHTML = resultsMarkup;
    this.setAttribute('results', true);
    this.setLiveRegionResults();
    this.open();
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const count = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (count) this.setLiveRegionText(count.textContent);
  }

  getResultsMaxHeight() {
    const header = document.querySelector('.section-header');
    this.resultsMaxHeight = window.innerHeight - (header ? header.getBoundingClientRect().bottom : 0);
    return this.resultsMaxHeight;
  }

  open() {
    this.predictiveSearchResults.style.maxHeight = this.resultsMaxHeight || `${this.getResultsMaxHeight()}px`;
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');
    if (selected) selected.setAttribute('aria-selected', false);
    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    this.predictiveSearchResults.removeAttribute('style');
  }
}

customElements.define('predictive-search', PredictiveSearch);
